// @ts-nocheck
import {
  Box,
  Card as MantineCard,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text as MantineText,
  ThemeIcon,
  Title,
  Code,
} from '@mantine/core';
import { IconCheck, IconPalette } from '@tabler/icons-react';
import {
  colorSchemes,
  getColorScheme,
  getPrimaryGradient,
  getSecondaryGradient,
  getTertiaryGradient,
} from '../../../theme/theme';
import { usePreferencesStore } from '../../../stores';
import { Badge, Button, Card, Divider, Progress, Text } from '../../../components/ui';

export default function ColorsPage() {
  const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
  const setColorScheme = usePreferencesStore((state) => state.setColorScheme);
  const currentScheme = getColorScheme(colorSchemeKey);

  return (
    <Stack gap="xl">
      <Box>
        <Text gradient gradientVariant="primary" size="xl" fw={700}>
          Color Schemes
        </Text>
        <MantineText c="dimmed" size="sm">
          Choose from {colorSchemes.length} beautiful color schemes with primary, secondary, and
          tertiary gradients. Click any scheme to apply it instantly.
        </MantineText>
      </Box>

      {/* Current Scheme Preview */}
      {currentScheme && (
        <MantineCard shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Group gap="sm">
                <ThemeIcon size="lg" radius="md" variant="light">
                  <IconPalette size={20} />
                </ThemeIcon>
                <Box>
                  <MantineText fw={600}>
                    Current Scheme: {currentScheme.emoji} {currentScheme.label}
                  </MantineText>
                  <MantineText size="xs" c="dimmed">
                    Click on any scheme below to change
                  </MantineText>
                </Box>
              </Group>
            </Group>

            <Group gap="lg">
              <Box>
                <MantineText size="xs" c="dimmed" mb={4}>
                  Primary
                </MantineText>
                <Box
                  style={{
                    width: 120,
                    height: 60,
                    borderRadius: 8,
                    background: getPrimaryGradient(currentScheme),
                  }}
                />
              </Box>
              <Box>
                <MantineText size="xs" c="dimmed" mb={4}>
                  Secondary
                </MantineText>
                <Box
                  style={{
                    width: 120,
                    height: 60,
                    borderRadius: 8,
                    background: getSecondaryGradient(currentScheme),
                  }}
                />
              </Box>
              <Box>
                <MantineText size="xs" c="dimmed" mb={4}>
                  Tertiary
                </MantineText>
                <Box
                  style={{
                    width: 120,
                    height: 60,
                    borderRadius: 8,
                    background: getTertiaryGradient(currentScheme),
                  }}
                />
              </Box>
            </Group>
          </Stack>
        </MantineCard>
      )}

      {/* Theme Gradient Components Demo */}
      <MantineCard shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={4}>Gradient Components Preview</Title>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Buttons:
            </MantineText>
            <Group gap="md">
              <Button gradientVariant="primary">Primary Button</Button>
              <Button gradientVariant="secondary">Secondary Button</Button>
              <Button gradientVariant="tertiary">Tertiary Button</Button>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
{`<Button gradientVariant="primary">Primary Button</Button>
<Button gradientVariant="secondary">Secondary Button</Button>
<Button gradientVariant="tertiary">Tertiary Button</Button>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Badges:
            </MantineText>
            <Group gap="sm">
              <Badge gradientVariant="primary">Primary</Badge>
              <Badge gradientVariant="secondary">Secondary</Badge>
              <Badge gradientVariant="tertiary">Tertiary</Badge>
              <Badge gradientVariant="success">Success</Badge>
              <Badge gradientVariant="warning">Warning</Badge>
              <Badge gradientVariant="danger">Danger</Badge>
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
{`<Badge gradientVariant="primary">Primary</Badge>
<Badge gradientVariant="success">Success</Badge>
<Badge gradientVariant="danger">Danger</Badge>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Progress Bars:
            </MantineText>
            <Stack gap="xs">
              <Progress gradientVariant="primary" value={75} />
              <Progress gradientVariant="secondary" value={60} />
              <Progress gradientVariant="tertiary" value={45} striped animated />
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
{`<Progress gradientVariant="primary" value={75} />
<Progress gradientVariant="secondary" value={60} />
<Progress gradientVariant="tertiary" value={45} striped animated />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>
              Gradient Text:
            </MantineText>
            <Stack gap="xs">
              <Text gradient gradientVariant="primary" size="xl" fw={700}>
                Primary Gradient Text
              </Text>
              <Text gradient gradientVariant="secondary" size="lg" fw={600}>
                Secondary Gradient Text
              </Text>
              <Text gradient gradientVariant="tertiary" size="md" fw={500}>
                Tertiary Gradient Text
              </Text>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>
              Code:
            </MantineText>
            <Code block language="tsx">
{`<Text gradient gradientVariant="primary" size="xl" fw={700}>
  Primary Gradient Text
</Text>`}
            </Code>
          </Stack>
        </Stack>
      </MantineCard>

      {/* All Color Schemes Grid */}
      <Box>
        <Title order={4} mb="md">
          All Color Schemes ({colorSchemes.length} Available)
        </Title>
        <SimpleGrid cols={{ base: 2, sm: 3, md: 5, lg: 6 }} spacing="md">
          {colorSchemes.map((scheme) => (
            <Paper
              key={scheme.key}
              p="sm"
              radius="md"
              withBorder
              style={{
                cursor: 'pointer',
                borderColor:
                  scheme.key === colorSchemeKey
                    ? 'var(--mantine-primary-color-filled)'
                    : undefined,
                borderWidth: scheme.key === colorSchemeKey ? 2 : 1,
                transition: 'all 0.2s',
              }}
              onClick={() => setColorScheme(scheme.key)}
            >
              <Stack gap="xs">
                <Box
                  style={{
                    width: '100%',
                    height: 40,
                    borderRadius: 6,
                    background: getPrimaryGradient(scheme),
                    position: 'relative',
                  }}
                >
                  {scheme.key === colorSchemeKey && (
                    <ThemeIcon
                      size="xs"
                      radius="xl"
                      color="white"
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        background: 'rgba(0,0,0,0.3)',
                      }}
                    >
                      <IconCheck size={10} />
                    </ThemeIcon>
                  )}
                </Box>
                <Group gap={4}>
                  <Box
                    style={{
                      flex: 1,
                      height: 16,
                      borderRadius: 4,
                      background: getSecondaryGradient(scheme),
                    }}
                  />
                  <Box
                    style={{
                      flex: 1,
                      height: 16,
                      borderRadius: 4,
                      background: getTertiaryGradient(scheme),
                    }}
                  />
                </Group>
                <MantineText size="xs" ta="center" lineClamp={1}>
                  {scheme.emoji} {scheme.label}
                </MantineText>
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>
      </Box>

      {/* Gradient Cards */}
      <Box>
        <Title order={4} mb="md">
          Gradient Cards
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          <Card variant="gradient" gradientVariant="primary" hoverEffect="lift">
            <Stack gap="xs">
              <MantineText fw={600} c="white">
                Primary Gradient Card
              </MantineText>
              <MantineText size="sm" c="white" opacity={0.9}>
                This card uses the primary gradient from the current color scheme with lift hover effect.
              </MantineText>
            </Stack>
          </Card>
          <Card variant="gradient" gradientVariant="secondary" hoverEffect="glow">
            <Stack gap="xs">
              <MantineText fw={600} c="white">
                Secondary Gradient Card
              </MantineText>
              <MantineText size="sm" c="white" opacity={0.9}>
                This card uses the secondary gradient with a glow hover effect.
              </MantineText>
            </Stack>
          </Card>
          <Card variant="gradient" gradientVariant="tertiary" hoverEffect="scale">
            <Stack gap="xs">
              <MantineText fw={600} c="white">
                Tertiary Gradient Card
              </MantineText>
              <MantineText size="sm" c="white" opacity={0.9}>
                This card uses the tertiary gradient with a scale hover effect.
              </MantineText>
            </Stack>
          </Card>
        </SimpleGrid>

        <Stack gap="xs" mt="md">
          <MantineText size="sm" fw={500}>
            Code:
          </MantineText>
          <Code block language="tsx">
{`<Card variant="gradient" gradientVariant="primary" hoverEffect="lift">
  <Text fw={600} c="white">Primary Gradient Card</Text>
  <Text size="sm" c="white" opacity={0.9}>
    Card content...
  </Text>
</Card>`}
          </Code>
        </Stack>
      </Box>
    </Stack>
  );
}
